import {
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  IMAGE_SRC,
  ITEMS_PAGE_LABEL,
  STATUS_OPTIONS,
  STOP_TYPE_OPTIONS,
} from 'src/app/components/shared/shared-constants';
import { TemplateMarkerDirective } from 'src/app/core/directives/template-marker/template-marker.directive';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  AddOnDemandPickupRequest,
  AddOnDemandPickupResponse,
  EditOnDemandPickupRequest,
  OnDemandPickupResponse,
} from 'src/app/core/models/operations/operations-on-demand-pickup.model';
import {
  AssignReassignRequest,
  OnDemandPickupBody,
  OperationsPickup,
  PickupColumn,
  SaveScheduledPickupRequest,
  SaveScheduledPickupResponse,
} from 'src/app/core/models/operations/operations-pickup.model';
import { ScheduledPickupResponse } from 'src/app/core/models/operations/operations-scheduled-pickup.model';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OnDemandPickupComponent } from './on-demand-pickup/on-demand-pickup.component';
import {
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  FLOOR,
  PACKAGE_TYPES,
  ROUTE_STATUSES,
  STATUSES,
  STOP_TYPES,
  SUITE,
} from './pickup.constants';
import { ScheduledPickupComponent } from './scheduled-pickup/scheduled-pickup.component';

@Component({
  selector: 'app-pickup',
  templateUrl: './pickup.component.html',
  styleUrls: ['./pickup.component.scss'],
  standalone: false,
})
export class PickupComponent implements OnInit, OnChanges {
  @Input()
  pickupData$!: Observable<OperationsPickup[]>;

  @Input()
  resetClick!: BehaviorSubject<boolean>;

  @Input()
  exportExcelEvent!: BehaviorSubject<boolean>;

  @Input()
  summaryRoutes: string[] = [];

  @Input()
  addPickupEvent!: EventEmitter<{
    response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
    data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
  }>;

  emitEnableClearFilterEvent = output<boolean>();
  refetchData = output<boolean>();

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  @ViewChild(MatSort) sort!: MatSort;

  @ContentChild(TemplateMarkerDirective) content!: TemplateMarkerDirective;

  selectedRouteId = '';
  loadedData = false;

  readonly ITEMS_PAGE_LABEL = ITEMS_PAGE_LABEL;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly HEADERS = ColumnText;
  readonly SRC = IMAGE_SRC;
  readonly SELECT_ALL_TEXT = STATUS_OPTIONS.ALL_SELECTED;
  readonly TOOLTIP_TEXT = STOP_TYPE_OPTIONS;
  readonly PACKAGES = PACKAGE_TYPES;
  readonly PICKUP_STATUS = STATUSES;
  readonly STOPS = STOP_TYPES;
  readonly STATUS = STATUS_OPTIONS;
  readonly SPECIAL_CHARS = SpecialCharacters;
  readonly IMAGES = IMAGE_SRC;

  dataSource: MatTableDataSource<OperationsPickup> =
    new MatTableDataSource<OperationsPickup>([]);

  isGroupEditMode = false;
  private readonly rowGroupMap = new Map<string, string>();
  private readonly groupedRowsByGroupId = new Map<string, string[]>();
  private readonly groupExpandedState = new Map<string, boolean>();
  private readonly selectedGroupRows = new Set<string>();
  private selectedGroupOrder: string[] = [];
  private rowSequence = 0;

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);

  routeStatusData: PickupColumn = {
    itemId: '',
    ids: [],
    checkAllIds: true,
    searchResults: ROUTE_STATUSES,
    formControl: new FormControl(''),
  };
  showRouteSortDropdown = false;

  stopTypeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STOP_TYPE,
  );

  routeData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.ROUTE);
  IDOsuggestionData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.IDO_SUGGESTION,
  );
  statusData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STATUS,
  );
  packageTypeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.PACKAGE_TYPE,
  );
  shipperPrnData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.SHIPPER_PRN,
  );
  streetAddressData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STREET_ADDRESS,
  );
  cityData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.CITY);
  postalData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.POSTAL,
  );
  readyTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.READY_TIME,
  );
  commitTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.COMMIT_TIME,
  );
  completeTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.COMPLETE_TIME,
  );

  showStreetAddressOptions = false;
  locationCode = '';
  locationGroup = '';
  assignReAssignRouteId = '';
  assignReassignOrderNumber = '';
  originalData!: OperationsPickup[];
  isIDO$ = new BehaviorSubject<boolean>(false);
  onDemandPickupData!: OnDemandPickupResponse;
  changedRoutes: OnDemandPickupBody[] = [];
  dropdownOpen = [false, false];
  stopTypeDropdownOpen = [false, false];

  constructor(
    public commonUtils: CommonUtils,
    private readonly scheduledDialog: MatDialog,
    private readonly pickupAssignmentsService: PickupAssignmentsService,
  ) {}

  ngOnInit() {
    this.dataSource.filterPredicate = this.pickupFilterPredicate;

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.locationCode = user?.location;
          this.locationGroup = user?.locationGroup;
        }),
      )
      .subscribe();

    this.resetClick
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
      )
      .subscribe(() => {
        this.clearAllFilters();
      });

    this.exportExcelEvent
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
        catchError(() => {
          this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_UNSUCCESSFUL);
          return of(false);
        }),
      )
      .subscribe(() => {
        this.exportExcel();
      });

    this.addPickupEvent
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(
          (data: {
            response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
            data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
          }) => {
            if (data?.response?.code === 200) {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.refetchData.emit(true);
            }
          },
        ),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subscribeToSummaryData(changes);
  }

  subscribeToSummaryData(changes: SimpleChanges) {
    const changedDataSource$: Observable<OperationsPickup[]> =
      changes.pickupData$?.currentValue;

    // Handle pickup data updates
    changedDataSource$
      ?.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((summary) => {
        this.originalData = summary.map((value, index) => ({
          ...value,
          rowUid: this.createRowUid(value, index),
          isRouteAssigned: !!value.route,
          addressCorrected: value.addressCorrected,
        }));

        this.resetGroupingState();
        this.dataSource.data = [...this.originalData];
        this.applyGroupFilter();
      });

    // Handle summary routes updates
    if (changes.summaryRoutes?.currentValue) {
      this.summaryRoutes = Array.from(
        new Set(changes.summaryRoutes?.currentValue),
      );
    }
  }

  convertToOperationPickup(
    data: SaveScheduledPickupRequest | AddOnDemandPickupRequest,
    response: SaveScheduledPickupResponse | AddOnDemandPickupResponse,
  ): OperationsPickup {
    // OnDemand pickup
    if ((data as AddOnDemandPickupRequest).onDemand) {
      return this.convertOnDemandPickupData(data as AddOnDemandPickupRequest);

      // Scheduled Pickup
    } else if ((data as SaveScheduledPickupRequest).schedulePickup) {
      return this.convertScheduledData(
        data as SaveScheduledPickupRequest,
        response,
      );
    } else {
      return {} as OperationsPickup;
    }
  }

  convertScheduledData(
    data: SaveScheduledPickupRequest,
    response: SaveScheduledPickupResponse,
  ): OperationsPickup {
    const custInfo = data.schedulePickup.customerInfo;

    const streetPrefix = custInfo.prefix ?? '';
    const streetNumber = custInfo.streetAddress ?? '';
    const streetName = custInfo.streetName ?? '';
    const suite = custInfo.suite ? `, ${SUITE} ${custInfo.suite}` : '';
    const floor = custInfo.floor ? `, ${FLOOR} ${custInfo.floor}` : '';
    const streetSuffix = custInfo.suffix ?? '';

    const streetAddress = `${streetPrefix} ${streetNumber} ${streetName} ${streetSuffix} ${suite} ${floor}`;

    return {
      id:
        data.schedulePickup.id !== 0
          ? data.schedulePickup.id
          : (response.response?.id ?? 0),
      route: data.schedulePickup.route,
      stopType: STOP_TYPES[1].id,
      IDOsuggestion: data.schedulePickup.IDOsuggestion ?? '',
      status: data.schedulePickup.status ?? '',
      packageType: data.schedulePickup.packageType ?? '',
      shipperPrn: custInfo?.shipperNo,
      customerName: custInfo?.customerName,
      streetAddress: streetAddress,
      city: custInfo?.cityTown,
      province: custInfo?.province,
      postal: custInfo?.postalCode,
      readyTime: data.schedulePickup.readyTime,
      commitTime: data.schedulePickup.commitTime,
      completeTime: data.schedulePickup.closingTime,
      isRouteAssigned: !!data.schedulePickup.route,
      countryCode: '',
    };
  }

  convertOnDemandPickupData(data: AddOnDemandPickupRequest): OperationsPickup {
    const streetPrefix = data.streetPrefix ? `${data.streetPrefix}` : '';
    const streetNumber = data.streetAddress ? `${data.streetAddress}` : '';
    const streetName = data.streetName ? `${data.streetName} ` : '';
    const streetType = data.streetType ? `${data.streetType}` : '';
    const suite = data.room ? `, ${SUITE} ${data.room}` : '';
    const floor = data.floor ? `, ${FLOOR} ${data.floor}` : '';
    const streetSuffix = data.streetSuffix ? `${data.streetSuffix}` : '';

    const streetAddress = `${streetPrefix} ${streetNumber} ${streetName} ${streetType} ${streetSuffix} ${suite} ${floor}`;
    return {
      id: 0,
      route: data.route,
      stopType: STOP_TYPES[0].id,
      IDOsuggestion: data.IDOSuggestion ?? '',
      status: data.status ?? '',
      packageType: data.packageType ?? '',
      shipperPrn: data.prnNr,
      customerName: data.customerName,
      streetAddress: streetAddress,
      city: data.politicalDivision2,
      province: data.politicalDivision1,
      postal: data.postalCode,
      readyTime: data.readyTime,
      commitTime: data.commitTime,
      completeTime: data.closingtime,
      isRouteAssigned: !!data.route,
      countryCode: '',
    };
  }

  updateRow(newValue: OperationsPickup) {
    this.originalData.forEach((dat) => {
      if (dat.id === newValue.id) {
        dat.route = newValue.route;
        dat.stopType = newValue.stopType;
        dat.IDOsuggestion = newValue.IDOsuggestion;
        dat.status = newValue.status;
        dat.packageType = newValue.packageType;
        dat.shipperPrn = newValue.shipperPrn;
        dat.customerName = newValue.customerName;
        dat.streetAddress = newValue.streetAddress;
        dat.city = newValue.city;
        dat.province = newValue.province;
        dat.postal = newValue.postal;
        dat.readyTime = newValue.readyTime;
        dat.commitTime = newValue.commitTime;
        dat.completeTime = newValue.completeTime;
        dat.addressCorrected = newValue.addressCorrected;
        dat.isRouteAssigned = !!newValue.route;
      }
    });
    this.dataSource.data = [...this.originalData];
    this.applyGroupFilter();
  }

  sortDataSource(direction: string) {
    this.originalData.sort((a, b) => {
      let comparatorResult = 0;

      if (a.route != null && b.route != null) {
        // Check if one route value is greater than the other; if equal, comparatorResult should remain 0.
        if (a.route > b.route) {
          comparatorResult = 1;
        } else if (a.route < b.route) {
          comparatorResult = -1;
        }
      } else if (direction === 'asc') {
        // check if sort by unassigned
        if (a.route != null) {
          comparatorResult = 1;
        } else if (b.route != null) {
          comparatorResult = -1;
        }
      } else {
        // check if sort by assigned
        if (a.route != null) {
          comparatorResult = -1;
        } else if (b.route != null) {
          comparatorResult = 1;
        }
      }

      return comparatorResult;
    });
    this.dataSource.data = [...this.originalData];
    this.applyGroupFilter();
  }

  exportExcel(): void {
    const filteredTable = this.dataSource.filteredData.map((data) => ({
        ROUTE: data.route,
        STATUS: data.status,
        'REQUEST/SHIPPER NO.': data.shipperPrn,
        'STOP TYPE': data.stopType,
        'READY TIME': data.readyTime,
        'COMPLETE TIME': data.completeTime,
        ADDRESS: `${data.customerName},${data.streetAddress},${data.streetAddress},${data.city},${data.province},${data.postal}`,
      }));
    this.commonUtils.exportExcel([], EXCEL_FILE_NAME, filteredTable);
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.routeData,
        this.IDOsuggestionData,
        this.shipperPrnData,
        this.streetAddressData,
        this.commitTimeData,
        this.cityData,
        this.postalData,
        this.readyTimeData,
        this.completeTimeData,
      ],
      this.dataSource,
      [this.stopTypeData, this.statusData, this.packageTypeData],
    );
    const filter = this.isFilterEnabled;
    this.emitEnableClearFilterEvent.emit(filter);
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  // Selecting route statuses
  routeStatusChanged() {
    const checkboxes = this.routeStatusData.searchResults.length;
    let selected = 0;
    this.routeStatusData.searchResults.forEach(
      (el: { selected: boolean; id: string }) => {
        if (el.selected) {
          selected++;
        }
      },
    );
    this.routeStatusData.checkAllIds = selected === checkboxes;
  }

  // Selecting all route statuses
  onAllRouteSelect(event: boolean) {
    this.routeStatusData.checkAllIds = event;
    this.commonUtils.selectAllForCheckedColumn(
      this.routeStatusData.searchResults,
      this.routeStatusData.checkAllIds,
    );
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      null,
      [
        this.routeData.formControl,
        this.IDOsuggestionData.formControl,
        this.shipperPrnData.formControl,
        this.streetAddressData.formControl,
        this.cityData.formControl,
        this.postalData.formControl,
        this.readyTimeData.formControl,
        this.commitTimeData.formControl,
        this.completeTimeData.formControl,
      ],
      [this.stopTypeData, this.statusData, this.packageTypeData],
    );
    this.dataSource.data = [...this.originalData];
    this.applyGroupFilter();
    this.resetSelectDropdowns.next(true);
    this.emitEnableClearFilterEvent.emit(false);
  }

  get isFilterEnabled() {
    const stopTypeItems = this.stopTypeData?.ids?.filter((item) => item);
    const stautsItems = this.statusData?.ids?.filter((item) => item);
    const packageTypeItems = this.packageTypeData?.ids?.filter((item) => item);

    return (
      stopTypeItems.length > 0 ||
      stautsItems.length > 0 ||
      packageTypeItems.length > 0 ||
      this.routeData?.formControl.value !== '' ||
      this.IDOsuggestionData?.formControl.value !== '' ||
      this.shipperPrnData?.formControl.value !== '' ||
      this.cityData?.formControl.value !== '' ||
      this.postalData?.formControl.value !== '' ||
      this.streetAddressData?.formControl.value !== '' ||
      this.readyTimeData?.formControl.value !== '' ||
      this.commitTimeData?.formControl.value !== '' ||
      this.completeTimeData?.formControl.value !== ''
    );
  }

  openScheduledPickup(pickup: OperationsPickup) {
    this.pickupAssignmentsService
      .getScheduledPickups(this.locationCode, pickup.shipperPrn, pickup.id)
      .pipe(take(1))
      .subscribe((data: ScheduledPickupResponse) => {
        if (data?.response?.schedulePickup) {
          this.scheduledDialog
            .open(ScheduledPickupComponent, {
              disableClose: true,
              minWidth: '66.75rem',
              minHeight: '37.5rem',
              data: {
                data: data,
                orderNumber: pickup.shipperPrn,
                routes: this.summaryRoutes,
                id: pickup.id,
                status: pickup.status,
                IDOsuggestion: pickup.IDOsuggestion,
                packageType: pickup.packageType,
                countryCode: pickup.countryCode,
                isIdoEnabled: pickup.idoEnabled,
              },
            })
            .afterClosed()
            .subscribe((data) => {
              if (data) {
                this.pickupAssignmentsService
                  .saveScheduledPickup(data, this.locationCode)
                  .pipe(take(1))
                  .subscribe((response) => {
                    if (response.code === 200) {
                      this.commonUtils.updateBannerType(
                        BANNER_TYPE.API_SUCCESS,
                      );

                      this.updateRow(
                        this.convertToOperationPickup(
                          data as SaveScheduledPickupRequest,
                          response,
                        ),
                      );
                    } else {
                      this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
                    }
                  });
              }
            });
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
      });
  }

  openOnDemandPickup(pickup: OperationsPickup) {
    this.pickupAssignmentsService
      .getOnDemandPickups(this.locationCode, pickup.shipperPrn)
      .pipe(take(1))
      .subscribe((data) => {
        if (data?.response?.onDemandPickup) {
          this.scheduledDialog
            .open(OnDemandPickupComponent, {
              disableClose: true,
              minWidth: '66.75rem',
              minHeight: '37.5rem',
              data: {
                data: data,
                status: pickup.status,
                IDOsuggestion: pickup.IDOsuggestion,
                packageType: pickup.packageType,
                countryCode: pickup.countryCode,
                locationGroup: this.locationGroup,
                locationCode: this.locationCode,
              },
            })
            .afterClosed()
            .subscribe(
              (data: {
                request: EditOnDemandPickupRequest;
                shipperPrn: string;
              }) => {
                if (data) {
                  this.pickupAssignmentsService
                    .editOnDemandPickup(data.request, this.locationCode)
                    .pipe(take(1))
                    .subscribe((response) => {
                      if (response.code === 200) {
                        this.commonUtils.updateBannerType(
                          BANNER_TYPE.API_SUCCESS,
                        );
                        this.convertToTableFormat(data);
                      } else {
                        this.commonUtils.updateBannerType(
                          BANNER_TYPE.API_ERROR,
                        );
                      }
                    });
                }
              },
            );
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
      });
  }

  convertToTableFormat(data: {
    request: EditOnDemandPickupRequest;
    shipperPrn: string;
  }) {
    const requestData = data.request.ondemandPickup;
    this.originalData.forEach((row) => {
      if (row.shipperPrn === data.shipperPrn) {
        row.city = requestData.cityTown;
        row.commitTime = requestData.commitTime;
        row.readyTime = requestData.readyTime;
        row.completeTime = requestData.closingTime;
        row.customerName = requestData.custName;
        row.province = requestData.province;
        row.postal = requestData.postalCode;

        const suite = requestData.suite ? `${SUITE} ${requestData.suite},` : '';
        const floor = requestData.floor ? `${FLOOR} ${requestData.floor}` : '';
        row.streetAddress = `${requestData.prefix} ${requestData.streetAddress} ${requestData.streetName} ${requestData.streetType} ${requestData.suffix} ${suite} ${floor}`;
      }
    });
    this.dataSource.data = [...this.originalData];
    this.applyGroupFilter();
  }

  reassignRoute(pickup: OperationsPickup) {
    pickup.isRouteAssigned = false;
  }

  changedRouteOptions(selectedRouteId: string, pickup: OperationsPickup) {
    const pickupBody: OnDemandPickupBody = {
      orderNumber: pickup.shipperPrn,
      route: selectedRouteId,
    };
    const valIndex = this.changedRoutes.findIndex(
      (val) => val.orderNumber === pickup.shipperPrn,
    );
    const val = this.changedRoutes[valIndex];

    if (valIndex > -1) {
      if (selectedRouteId !== undefined) {
        val.route = selectedRouteId;
      } else {
        this.changedRoutes.splice(valIndex, 1);
      }
    } else {
      this.changedRoutes.push(pickupBody);
    }
  }

  assignReassignRoute() {
    const request: AssignReassignRequest = {
      ondemandPickup: this.changedRoutes,
    };
    this.pickupAssignmentsService
      .assignReassign(request, this.locationCode)
      .pipe(
        take(1),
        filter((data) => data.code === 200),
      )
      .subscribe(() => {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        this.changedRoutes = [];
        this.refetchData.emit(true);
      });
  }

  toggleGroupEditMode(): void {
    if (!this.isGroupEditMode) {
      this.isGroupEditMode = true;
      this.selectedGroupRows.clear();
      this.selectedGroupOrder = [];
      this.groupedRowsByGroupId.forEach((members) => {
        members.forEach((rowUid) => {
          this.selectedGroupRows.add(rowUid);
          this.selectedGroupOrder.push(rowUid);
        });
      });
      this.applyGroupFilter();
      return;
    }

    this.applyGroupingFromSelection();
    this.isGroupEditMode = false;
    this.selectedGroupRows.clear();
    this.selectedGroupOrder = [];
    this.applyGroupFilter();
  }

  isRowSelectedForGrouping(row: OperationsPickup): boolean {
    return !!row.rowUid && this.selectedGroupRows.has(row.rowUid);
  }

  onGroupSelectionChange(row: OperationsPickup, checked: boolean): void {
    if (!row.rowUid) {
      return;
    }

    if (checked) {
      if (!this.selectedGroupRows.has(row.rowUid)) {
        this.selectedGroupOrder.push(row.rowUid);
      }
      this.selectedGroupRows.add(row.rowUid);
      return;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    if (!groupId) {
      this.selectedGroupRows.delete(row.rowUid);
      return;
    }

    const members = this.groupedRowsByGroupId.get(groupId) ?? [];
    if (members[0] === row.rowUid) {
      members.forEach((memberUid) => {
        this.selectedGroupRows.delete(memberUid);
        this.removeFromSelectedOrder(memberUid);
      });
      return;
    }

    this.selectedGroupRows.delete(row.rowUid);
    this.removeFromSelectedOrder(row.rowUid);
  }

  isGroupedLeader(row: OperationsPickup): boolean {
    if (!row.rowUid) {
      return false;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    if (!groupId) {
      return false;
    }

    const members = this.groupedRowsByGroupId.get(groupId) ?? [];
    return members[0] === row.rowUid;
  }

  showGroupChevron(row: OperationsPickup): boolean {
    if (!row.rowUid || this.isGroupEditMode || !this.isGroupedLeader(row)) {
      return false;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    return !!groupId && (this.groupedRowsByGroupId.get(groupId)?.length ?? 0) > 1;
  }

  isGroupedRowExpanded(row: OperationsPickup): boolean {
    if (!row.rowUid) {
      return false;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    return !!groupId && (this.groupExpandedState.get(groupId) ?? false);
  }

  getGroupCountLabel(row: OperationsPickup): string {
    if (!row.rowUid || !this.isGroupedLeader(row)) {
      return '';
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    const count = (this.groupedRowsByGroupId.get(groupId ?? '')?.length ?? 1) - 1;
    return `(${count < 10 ? '0' : ''}${count})`;
  }

  toggleGroupedRow(row: OperationsPickup): void {
    if (!this.showGroupChevron(row) || !row.rowUid) {
      return;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    if (!groupId) {
      return;
    }

    const current = this.groupExpandedState.get(groupId) ?? false;
    this.groupExpandedState.set(groupId, !current);
    this.applyGroupFilter();
  }

  private applyGroupingFromSelection(): void {
    this.rowGroupMap.clear();
    this.groupedRowsByGroupId.clear();
    this.groupExpandedState.clear();

    const members = this.selectedGroupOrder
      .filter((rowUid) => this.selectedGroupRows.has(rowUid))
      .filter((rowUid, index, arr) => arr.indexOf(rowUid) === index)
      .filter((rowUid) =>
        this.originalData.some((row) => row.rowUid === rowUid),
      );

    if (members.length < 2) {
      return;
    }

    const groupId = `group-${this.rowSequence++}`;
    this.groupedRowsByGroupId.set(groupId, members);
    this.groupExpandedState.set(groupId, false);
    members.forEach((rowUid) => this.rowGroupMap.set(rowUid, groupId));
  }

  private resetGroupingState(): void {
    this.isGroupEditMode = false;
    this.rowGroupMap.clear();
    this.groupedRowsByGroupId.clear();
    this.groupExpandedState.clear();
    this.selectedGroupRows.clear();
    this.selectedGroupOrder = [];
  }

  private removeFromSelectedOrder(rowUid: string): void {
    this.selectedGroupOrder = this.selectedGroupOrder.filter(
      (selectedUid) => selectedUid !== rowUid,
    );
  }

  private createRowUid(row: OperationsPickup, index: number): string {
    const idPart = row.id ?? 'new';
    const prn = row.shipperPrn ?? 'no-prn';
    return `${idPart}-${prn}-${index}`;
  }

  private applyGroupFilter(): void {
    const current = this.dataSource.filter;
    this.dataSource.filter =
      current && current.trim().length > 0 ? current : '{}';
  }

  private pickupFilterPredicate = (
    row: OperationsPickup,
    filter: string,
  ): boolean => {
    const filterValues = this.parseFilter(filter);
    if (!this.rowMatchesColumns(row, filterValues)) {
      return false;
    }

    if (this.isGroupEditMode || !row.rowUid) {
      return true;
    }

    const groupId = this.rowGroupMap.get(row.rowUid);
    if (!groupId) {
      return true;
    }

    const members = this.groupedRowsByGroupId.get(groupId) ?? [];
    const isLeader = members[0] === row.rowUid;
    const expanded = this.groupExpandedState.get(groupId) ?? false;

    return isLeader || expanded;
  };

  private parseFilter(filter: string): Record<string, unknown> {
    try {
      return filter ? JSON.parse(filter) : {};
    } catch {
      return {};
    }
  }

  /** Mirrors CommonUtils.createFilter column-matching logic for a pickup row. */
  private rowMatchesColumns(
    data: OperationsPickup,
    filterValues: Record<string, unknown>,
  ): boolean {
    return Object.keys(filterValues).every((filterKey) => {
      const value = filterValues[filterKey];
      const rowValue = (data as unknown as Record<string, unknown>)[filterKey];
      const searchData = (rowValue as string)?.toString().toLowerCase() || '';
      const searchValue = value?.toString().toLowerCase() ?? '';

      const isMultiSelect = searchValue.includes(',');
      return isMultiSelect
        ? [...(value as string[])].find(
            (item: string) => item.toLowerCase() === searchData,
          ) !== undefined
        : searchData.includes(searchValue);
    });
  }
}
